'use client'

import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVerticalIcon, Trash2Icon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

type PropsType = {
  id: string
  index: number
  name: string
  email: string
  onNameChange: (value: string) => void
  onEmailChange: (value: string) => void
  onRemove: () => void
  labels: {
    email: string
    name: string
  }
  nameError?: string
  emailError?: string
  className?: string
  inputsClassName?: string
}

export const SortableEmailItem = ({
  id,
  index,
  name,
  email,
  onNameChange,
  onEmailChange,
  onRemove,
  labels,
  nameError,
  emailError,
  className = '',
  inputsClassName = ''
}: PropsType) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  }

  return (
    <div ref={setNodeRef} style={style} className={`rounded-lg border p-4 ${className}`}>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div {...attributes} {...listeners} className="flex cursor-grab items-center active:cursor-grabbing">
            <GripVerticalIcon className="text-muted-foreground h-5 w-5" />
          </div>
          <span className="text-sm font-medium">
            {labels.email} #{index + 1}
          </span>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={onRemove}>
          <Trash2Icon className="h-4 w-4" />
        </Button>
      </div>
      <div className={`grid grid-cols-1 gap-3 xl:grid-cols-2 ${inputsClassName}`}>
        <div className="space-y-1">
          <label className="text-sm font-medium">{labels.name}</label>
          <Input
            placeholder={labels.name}
            value={name}
            onChange={e => onNameChange(e.target.value)}
            aria-invalid={!!nameError}
            data-invalid={!!nameError || undefined}
          />
          {nameError && (
            <p className="text-destructive text-sm" role="alert" aria-live="polite">
              {nameError}
            </p>
          )}
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium">{labels.email}</label>
          <Input
            type="email"
            placeholder={labels.email}
            value={email}
            onChange={e => onEmailChange(e.target.value)}
            aria-invalid={!!emailError}
            data-invalid={!!emailError || undefined}
          />
          {emailError && (
            <p className="text-destructive text-sm" role="alert" aria-live="polite">
              {emailError}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
